import type { Filter, Sorting } from '@/models/objects/datasetViewState';
import type { SqlQueryResults, SqlQueryResultsWithCols } from '@/services/types';
import type { CacheAwareStartResult, CacheParams } from '@/services/queryCache/types';
import type Dataset from './dataset';
import type DatasetTest from './datasetTest';
import DataObject from './dataObject';
import { graphql, subscribe } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { handleUnknownError } from '@/utils/handleError';
import {
  noLint_waitForRedshiftQueryUntilDoneAndReturnResults,
  decompressIfNeededThenJsonParse,
} from '@/woLint/services/dataset.common';

const ns = 'app.services.dataset.common';

export async function testSqlQuery(
  sqlQuery: string,
  pageSize: number,
  startIndex: number,
  filters: Filter[] = [],
  sortings: Sorting[] = [],
  onSuccess?: (executionId: string) => void,
  wrapperQuery?: string,
  displayColumns?: string[],
): Promise<{ executionId: string }> {
  const { executionId } = await graphql('beemAgentPreviewDatasetQuery', {
    query: {
      sqlQuery, // note that beem-agent doesn't expect a clean query since it will call minify() etc.. on that side
      pageSize,
      startIndex,
      filters: JSON.stringify(filters),
      sortings: JSON.stringify(sortings),
      wrapperQuery,
      ...(displayColumns ? { displayColumns: JSON.stringify(displayColumns) } : {}),
    },
  });
  // Without cacheParams, beem-agent always starts a Redshift run and returns an executionId.
  // executionId is non-null in the schema contract (String!), so no assertion is needed here.
  logger.info({ label: `${ns}.testSqlQuery.executionId`, message: executionId });
  if (onSuccess) onSuccess(executionId);
  return { executionId };
}

/**
 * Cache-aware variant of the query start used by dashboard visualization widgets.
 *
 * Sends `cacheParams` so beem-agent can consult/write the read-through cache. Returns
 * a discriminated result: either an inline cache hit (rows/cols already present, no
 * polling) or a started Redshift run (poll with the executionId; forward cacheContext
 * to the check call so the finished result is cached). See CacheAwareStartResult.
 *
 * `onExecutionId` mirrors testSqlQuery's onSuccess so callers can capture the
 * executionId for cancellation; it only fires on the 'run' path.
 */
export async function testSqlQueryWithCache(
  sqlQuery: string,
  pageSize: number,
  startIndex: number,
  filters: Filter[] = [],
  cacheParams: CacheParams,
  wrapperQuery?: string,
  displayColumns?: string[],
  onExecutionId?: (executionId: string) => void,
): Promise<CacheAwareStartResult> {
  const res = await graphql('beemAgentPreviewDatasetQuery', {
    query: {
      sqlQuery,
      pageSize,
      startIndex,
      filters: JSON.stringify(filters),
      sortings: JSON.stringify([]),
      wrapperQuery,
      ...(displayColumns ? { displayColumns: JSON.stringify(displayColumns) } : {}),
      cacheParams: JSON.stringify(cacheParams),
    },
  });

  // Inline cache hit: rows/cols are present inline and there is no run to poll.
  // Primary signal is `fromCache`, but we also treat "rows + cols present AND no
  // executionId" as an inline hit. That combination is unambiguous — a started run always
  // carries an executionId and never inline rows/cols — so this stays robust if a future
  // beem-agent returns the inline payload without setting `fromCache`, instead of falling
  // through to the no-executionId error path below. The null checks stay inline in the
  // condition so TypeScript narrows res.rows/res.cols to string inside the block.
  if (res.rows != null && res.cols != null && (res.fromCache || !res.executionId)) {
    logger.info({ label: `${ns}.testSqlQueryWithCache.inlineHit`, message: cacheParams.vizId });
    return {
      kind: 'inline',
      fromCache: true,
      stale: Boolean(res.stale),
      createdAt: res.createdAt ?? 0,
      cols: decompressIfNeededThenJsonParse(res.cols) as SqlQueryResultsWithCols['cols'],
      rows: decompressIfNeededThenJsonParse(res.rows) as SqlQueryResultsWithCols['rows'],
    };
  }

  // Started a Redshift run — poll as usual.
  if (!res.executionId) {
    throw new Error('BEEM-cache: preview response had neither cache rows nor an executionId');
  }
  logger.info({ label: `${ns}.testSqlQueryWithCache.executionId`, message: res.executionId });
  if (onExecutionId) onExecutionId(res.executionId);
  return {
    kind: 'run',
    executionId: res.executionId,
    cacheContext: res.cacheContext ?? undefined,
  };
}

// vinh1768
export async function getContentSync(
  this: Dataset,
  pageSize: number,
  startIndex: number,
  filters: Filter[] = [],
  sortings: Sorting[] = [],
): Promise<SqlQueryResults> {
  const { rows } = await graphql('beemAgentGetDatasetContentSync', {
    query: {
      cloudResourceId: this.cloudResourceId,
      version: String(this.version), // should be a number but it doesn't hurt, we can refactor later for consistency
      pageSize,
      startIndex,
      filters: JSON.stringify(filters),
      sortings: JSON.stringify(sortings),
    },
  });
  return decompressIfNeededThenJsonParse(rows) as SqlQueryResults;
}

export async function getContentStart(
  this: DataObject | Dataset | DatasetTest,
  pageSize: number,
  startIndex: number,
  filters: Filter[] = [],
  sortings: Sorting[] = [],
  onSuccess?: (executionId: string) => void,
): Promise<{ executionId: string }> {
  const command =
    this instanceof DataObject ? 'beemAgentGetDataObjectContent' : 'beemAgentGetDatasetContent';
  const data = await graphql(command, {
    query: {
      cloudResourceId: this.cloudResourceId,
      version: this instanceof DataObject ? '' : String(this.version), // should be a number but it doesn't hurt, we can refactor later for consistency
      pageSize,
      startIndex,
      filters: JSON.stringify(filters),
      sortings: JSON.stringify(sortings),
    },
  });
  if (onSuccess) onSuccess(data.executionId);
  return data;
}

export function listenToPipelineResult(
  this: Dataset | DatasetTest,
  onSuccess: () => void,
  onFailure: () => void,
): void {
  const { cloudResourceId } = this;
  subscribe('onCreatePipelineResult', {}, (result, done) => {
    if (result.recordCloudResourceId === cloudResourceId) {
      if (result.status === 'SUCCESS') onSuccess();
      else onFailure();
      done();
    }
  });
}

export async function waitForRedshiftQueryUntilDoneAndReturnResults(query: {
  executionId: string;
  cacheContext?: string;
}): Promise<SqlQueryResultsWithCols> {
  return noLint_waitForRedshiftQueryUntilDoneAndReturnResults(query);
}

export async function cancelRedshiftQueryAndIgnoreError(query: {
  executionId: string;
}): Promise<void> {
  try {
    const data = await graphql('beemAgentCancelRedshiftQuery', { query });
    logger.debug({ label: 'cancelRedshiftQuery', message: data });
  } catch (e) {
    handleUnknownError(e);
    logger.debug({ label: 'cancelRedshiftQuery', message: 'errored' });
  }
}
